import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Badge,
  Icon,
  Grid,
  GridItem,
  Flex,
  Image,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Stack,
  Divider,
  SimpleGrid,
  Alert,
  AlertIcon,
  Link,
} from '@chakra-ui/react';
import {
  CalendarIcon,
  ExternalLinkIcon,
  StarIcon,
  InfoIcon,
  ViewIcon,
} from '@chakra-ui/icons';
import { 
  getAllContests, 
  getContestsByStatus, 
  getContestStatusDisplay, 
  getContestStatusColor,
  Contest 
} from '../utils/contestUtils';

const ContestList: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  
  const allContests = getAllContests();
  const activeContests = getContestsByStatus('active');
  const upcomingContests = getContestsByStatus('upcoming');
  const completedContests = getContestsByStatus('completed');
  const historicalContests = getContestsByStatus('historical');

  const ContestCard: React.FC<{ contest: Contest; featured?: boolean }> = ({ contest, featured = false }) => {
    const statusColor = getContestStatusColor(contest);
    const statusDisplay = getContestStatusDisplay(contest);
    
    return (
      <Card
        bg={cardBg}
        borderColor={borderColor}
        borderWidth="1px"
        shadow={featured ? 'xl' : 'md'}
        transform={featured ? 'scale(1.02)' : 'scale(1)'}
        transition="all 0.2s ease"
        _hover={{
          shadow: 'lg',
          transform: featured ? 'scale(1.03)' : 'scale(1.01)',
        }}
        position="relative"
        overflow="hidden"
      >
        {featured && (
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            height="4px"
            bgGradient="linear(to-r, purple.400, pink.400)"
          />
        )}
        
        <CardHeader>
          <VStack align="start" spacing={2}>
            <HStack justify="space-between" width="100%">
              <Badge colorScheme={statusColor} variant="solid" fontSize="xs">
                {statusDisplay}
              </Badge>
              {featured && (
                <Badge colorScheme="purple" variant="outline" fontSize="xs">
                  <Icon as={StarIcon} mr={1} />
                  Featured
                </Badge>
              )}
            </HStack>
            
            <Heading 
              size="md" 
              color={contest.theme.primaryColor}
              as={RouterLink}
              to={`/contest/${contest.id}`}
              _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {contest.name}
            </Heading>
            
            {contest.venue && (
              <HStack spacing={1}>
                <Icon as={CalendarIcon} color={textSecondary} />
                <Text fontSize="sm" color={textSecondary}>
                  {contest.venue}
                </Text>
              </HStack>
            )}
          </VStack>
        </CardHeader>

        <CardBody pt={0}>
          <Text color={textSecondary} mb={4}>
            {contest.description}
          </Text>
          
          <VStack align="start" spacing={3}>
            <HStack spacing={4}>
              <Text fontSize="sm" fontWeight="semibold">
                Problems:
              </Text>
              <Text fontSize="sm" color={textSecondary}>
                {contest.problems.length} challenge{contest.problems.length !== 1 ? 's' : ''}
                {contest.problems.length > 1 && (
                  <Text as="span" ml={2} fontSize="xs" color="blue.500">
                    (BFS & SSSP)
                  </Text>
                )}
              </Text>
            </HStack>
            
            {contest.startDate && contest.endDate && (
              <HStack spacing={1}>
                <Icon as={CalendarIcon} color={textSecondary} />
                <Text fontSize="sm" color={textSecondary}>
                  {contest.startDate.toLocaleDateString()} - {contest.endDate.toLocaleDateString()}
                </Text>
              </HStack>
            )}
          </VStack>
        </CardBody>

        <CardFooter pt={0}>
          <HStack spacing={3} width="100%">
            <Button
              as={RouterLink}
              to={`/contest/${contest.id}`}
              colorScheme="blue"
              size="sm"
              variant="solid"
              leftIcon={<InfoIcon />}
              flex={1}
            >
              View Contest
            </Button>
            
            {contest.problems.length > 0 && (
              <Button
                as={RouterLink}
                to={`/problem/${contest.problems[0]}`}
                size="sm"
                variant="outline"
                colorScheme="blue"
                leftIcon={<ViewIcon />}
              >
                Start Solving
              </Button>
            )}
            
            {contest.leaderboardUrl && (
              <Button
                as={RouterLink}
                to={contest.leaderboardUrl}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                leftIcon={<StarIcon />}
              >
                Leaderboard
              </Button>
            )}
          </HStack>
        </CardFooter>
      </Card>
    );
  };

  const ContestSection: React.FC<{ title: string; contests: Contest[]; emptyMessage: string }> = ({ 
    title, 
    contests, 
    emptyMessage 
  }) => (
    <VStack align="start" spacing={6} width="100%">
      <Heading size="lg">{title}</Heading>
      {contests.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} width="100%">
          {contests.map((contest) => (
            <ContestCard
              key={contest.id}
              contest={contest}
              featured={contest.status === 'historical'}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {emptyMessage}
        </Alert>
      )}
    </VStack>
  );

  return (
    <Box bg={bgColor} minHeight="100vh">
      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header Section */}
          <VStack spacing={4} textAlign="center">
            <Heading size="2xl" bgGradient="linear(to-r, blue.400, purple.500)" bgClip="text">
              Speedcode Contests
            </Heading>
            <Text fontSize="lg" color={textSecondary} maxW="2xl">
              Challenge yourself with competitive programming contests focused on performance optimization and parallel computing.
            </Text>
          </VStack>

          {/* Special Events Highlight */}
          {historicalContests.length > 0 && (
            <Box>
              <VStack spacing={6} align="start">
                <HStack spacing={3}>
                  <Icon as={StarIcon} color="purple.500" />
                  <Heading size="lg" color="purple.500">
                    Special Events
                  </Heading>
                </HStack>
                <Text color={textSecondary}>
                  Featured competitions that showcase the best of performance engineering challenges.
                </Text>
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} width="100%">
                  {historicalContests.map((contest) => (
                    <ContestCard key={contest.id} contest={contest} featured={true} />
                  ))}
                </SimpleGrid>
              </VStack>
              <Divider my={8} />
            </Box>
          )}

          {/* Contest Tabs */}
          <Tabs 
            index={selectedTab} 
            onChange={setSelectedTab}
            variant="line"
            colorScheme="blue"
          >
            <TabList>
              <Tab>All Contests ({allContests.length})</Tab>
              <Tab>Active ({activeContests.length})</Tab>
              <Tab>Upcoming ({upcomingContests.length})</Tab>
              <Tab>Completed ({completedContests.length})</Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                <ContestSection
                  title="All Contests"
                  contests={allContests.filter(c => c.status !== 'historical')}
                  emptyMessage="No contests are currently available. Check back soon for exciting new challenges!"
                />
              </TabPanel>
              
              <TabPanel px={0}>
                <ContestSection
                  title="Active Contests"
                  contests={activeContests}
                  emptyMessage="No contests are currently active. Check the upcoming tab for future contests!"
                />
              </TabPanel>
              
              <TabPanel px={0}>
                <ContestSection
                  title="Upcoming Contests"
                  contests={upcomingContests}
                  emptyMessage="No upcoming contests scheduled at this time. Follow our announcements for updates!"
                />
              </TabPanel>
              
              <TabPanel px={0}>
                <ContestSection
                  title="Completed Contests"
                  contests={completedContests}
                  emptyMessage="No completed contests to display yet."
                />
              </TabPanel>
            </TabPanels>
          </Tabs>

          {/* Info Section */}
          <Box mt={12}>
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
              <CardBody>
                <VStack spacing={4} align="start">
                  <Heading size="md">About Speedcode Contests</Heading>
                  <Text color={textSecondary}>
                    Speedcode contests are designed to challenge your performance optimization skills. 
                    Each contest focuses on different aspects of high-performance computing, including 
                    parallel algorithms, memory optimization, and computational efficiency.
                  </Text>
                  <Text color={textSecondary} fontSize="sm">
                    <strong>Tip:</strong> Click "View Contest" to see all problems in a contest and access 
                    detailed information, datasets, and evaluation criteria.
                  </Text>
                  <HStack spacing={6} flexWrap="wrap">
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="semibold" fontSize="sm">
                        Competition Format
                      </Text>
                      <Text fontSize="sm" color={textSecondary}>
                        Algorithm optimization challenges
                      </Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="semibold" fontSize="sm">
                        Languages
                      </Text>
                      <Text fontSize="sm" color={textSecondary}>
                        C++ with OpenCilk
                      </Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="semibold" fontSize="sm">
                        Focus Areas
                      </Text>
                      <Text fontSize="sm" color={textSecondary}>
                        Parallel computing & performance
                      </Text>
                    </VStack>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default ContestList; 